import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { Controller, useForm, type Path } from 'react-hook-form';
import { z } from 'zod';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { useNotify } from '../../lib/notify';
import {
  attributeErrors,
  catalogFieldErrors,
  catalogKeys,
  emptyVariant,
  idempotency,
  productDetailsSchema,
  productToDetailsForm,
  productUnitToRow,
  suggestSku,
  toCreateVariantRequest,
  toUnitInputs,
  toUpdateProductRequest,
  toUpdateVariantRequest,
  unitRowErrors,
  unitRowSchema,
  variantFormSchema,
  variantToForm,
  type Product,
  type ProductDetailsForm,
  type UnitRow,
  type Variant,
  type VariantForm,
  EMPTY_SELECT_SLOTS,
} from './model';
import { PosFields } from './PosFields';
import { useAttributes, useBrands, useCategories, useUnits } from './queries';
import { CategorySelect } from './ui';
import { UnitRowsEditor } from './UnitRowsEditor';
import { VariantFields } from './VariantFields';

export function FormDialog({
  open,
  title,
  subtitle,
  busy,
  submitLabel,
  maxWidth = 'sm',
  onClose,
  onSubmit,
  children,
}: {
  open: boolean;
  title: string;
  subtitle?: ReactNode;
  busy: boolean;
  submitLabel: string;
  maxWidth?: 'sm' | 'md';
  onClose: () => void;
  onSubmit: () => void;
  children: ReactNode;
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullScreen={fullScreen}
      maxWidth={maxWidth}
      fullWidth
    >
      <Box
        component="form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
            e.preventDefault();
            onSubmit();
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: subtitle ? 0.5 : undefined }}>{title}</DialogTitle>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 3, mb: 1 }}>
            {subtitle}
          </Typography>
        )}
        <DialogContent sx={{ pt: '12px !important' }}>{children}</DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={busy}>
            {submitLabel}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

// --- product details ---------------------------------------------------------------------

export function ProductEditDialog({
  open,
  product,
  onClose,
}: {
  open: boolean;
  product: Product;
  onClose: () => void;
}) {
  const notify = useNotify();
  const qc = useQueryClient();
  const categories = useCategories();
  const brands = useBrands();
  const { register, control, handleSubmit, reset, setError, formState } =
    useForm<ProductDetailsForm>({
      resolver: zodResolver(productDetailsSchema),
      defaultValues: productToDetailsForm(product),
      mode: 'onBlur',
    });
  const { errors } = formState;
  useEffect(() => {
    if (open) reset(productToDetailsForm(product));
  }, [open, product, reset]);

  const save = useMutation({
    mutationFn: (f: ProductDetailsForm) =>
      unwrap(
        api.PATCH('/products/{id}', {
          params: { path: { id: product.id } },
          body: toUpdateProductRequest(f, product.version),
        }),
      ),
    onSuccess: async (saved) => {
      notify.success(`${saved.name} updated`);
      qc.setQueryData(catalogKeys.product(saved.id), saved);
      await qc.invalidateQueries({ queryKey: catalogKeys.products });
      onClose();
    },
    onError: (e) => {
      const fields = catalogFieldErrors(e);
      for (const f of fields)
        setError(f.path as Path<ProductDetailsForm>, { type: 'server', message: f.message });
      if (fields.length === 0) notify.error(e);
    },
  });

  const option = (kind: 'BRAND' | 'MANUFACTURER', current: string) =>
    (brands.data ?? []).filter((b) => b.kind === kind && (b.isActive || b.id === current));

  return (
    <FormDialog
      open={open}
      title={`Edit ${product.name}`}
      subtitle="Posted documents keep the name and code they were created with."
      busy={save.isPending}
      submitLabel="Save changes"
      onClose={onClose}
      onSubmit={() => void handleSubmit((v) => save.mutate(v))()}
    >
      <Stack spacing={2.25}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Product name"
            required
            fullWidth
            {...register('name')}
            error={!!errors.name}
            helperText={errors.name?.message}
          />
          <TextField
            label="Product code"
            required
            {...register('code')}
            error={!!errors.code}
            helperText={errors.code?.message}
            sx={{ minWidth: { sm: 180 } }}
          />
        </Stack>
        <TextField
          label="Description"
          multiline
          minRows={2}
          {...register('description')}
          error={!!errors.description}
          helperText={errors.description?.message}
        />
        <Controller
          control={control}
          name="categoryId"
          render={({ field }) => (
            <CategorySelect
              label="Category"
              categories={categories.data}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {(['brandId', 'manufacturerId'] as const).map((key) => (
            <Controller
              key={key}
              control={control}
              name={key}
              render={({ field }) => (
                <TextField
                  select
                  fullWidth
                  label={key === 'brandId' ? 'Brand' : 'Manufacturer'}
                  slotProps={EMPTY_SELECT_SLOTS}
                  value={field.value}
                  onChange={field.onChange}
                >
                  <MenuItem value="">
                    <Typography color="text.secondary">Not set</Typography>
                  </MenuItem>
                  {option(key === 'brandId' ? 'BRAND' : 'MANUFACTURER', field.value).map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          ))}
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ alignItems: { sm: 'center' } }}
        >
          <Controller
            control={control}
            name="trackSerial"
            render={({ field }) => (
              <FormControlLabel
                sx={{ flex: 1 }}
                control={
                  <Switch
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Record serial numbers"
              />
            )}
          />
          <TextField
            label="Warranty"
            {...register('warrantyMonths')}
            error={!!errors.warrantyMonths}
            helperText={errors.warrantyMonths?.message}
            slotProps={{
              input: { endAdornment: <InputAdornment position="end">months</InputAdornment> },
              htmlInput: { inputMode: 'numeric' },
            }}
            sx={{ width: { sm: 180 } }}
          />
        </Stack>
        <PosFields control={control} />
        <Alert severity="info" variant="outlined">
          Base unit ({product.baseUnit.name}) and stock tracking can’t change after creation.
        </Alert>
      </Stack>
    </FormDialog>
  );
}

// --- variant -----------------------------------------------------------------------------

const variantDialogSchema = z.object({ variants: z.array(variantFormSchema).length(1) });
type VariantDialogForm = z.infer<typeof variantDialogSchema>;

/** Add a variant to a product, or edit one (`variant` given). */
export function VariantDialog({
  open,
  product,
  variant,
  onClose,
}: {
  open: boolean;
  product: Product;
  variant: Variant | null;
  onClose: () => void;
}) {
  const notify = useNotify();
  const qc = useQueryClient();
  const { can } = useAuth();
  const attributes = useAttributes();
  const defs = attributes.data ?? [];
  const canViewCost = can('products.view_cost');
  const canPrice = variant ? can('products.update_price') : true;
  const initial = (): VariantDialogForm => ({
    variants: [variant ? variantToForm(variant) : emptyVariant()],
  });
  const { control, handleSubmit, reset, setError, setValue, getValues } =
    useForm<VariantDialogForm>({
      resolver: zodResolver(variantDialogSchema),
      defaultValues: initial(),
      mode: 'onBlur',
    });
  useEffect(() => {
    if (open) reset(initial());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when opened for another variant
  }, [open, variant]);

  const save = useMutation({
    mutationFn: (f: VariantForm) =>
      variant
        ? unwrap(
            api.PATCH('/variants/{id}', {
              params: { path: { id: variant.id } },
              body: toUpdateVariantRequest(f, variant, defs, { canPrice, canViewCost }),
            }),
          )
        : unwrap(
            api.POST('/products/{id}/variants', {
              params: { path: { id: product.id }, header: idempotency() },
              body: toCreateVariantRequest(f, defs, { canViewCost }),
            }),
          ),
    onSuccess: async (saved) => {
      notify.success(variant ? `${saved.displayName} updated` : `${saved.displayName} added`);
      await qc.invalidateQueries({ queryKey: catalogKeys.products });
      onClose();
    },
    onError: (e) => {
      const fields = catalogFieldErrors(e, getValues('variants'));
      for (const f of fields) {
        // A single-variant request reports "sku"; the dialog keeps it at variants.0.
        const path = f.path.startsWith('variants.') ? f.path : `variants.0.${f.path}`;
        setError(path as Path<VariantDialogForm>, { type: 'server', message: f.message });
      }
      if (fields.length === 0) notify.error(e);
    },
  });

  const submit = () =>
    void handleSubmit((v) => {
      const f = v.variants[0];
      if (!f) return;
      const errs = attributeErrors(f.attributes, defs);
      for (const [id, message] of Object.entries(errs))
        setError(`variants.0.attributes.${id}`, { type: 'rule', message });
      if (Object.keys(errs).length === 0) save.mutate(f);
    })();

  return (
    <FormDialog
      open={open}
      maxWidth="md"
      title={variant ? `Edit ${variant.displayName}` : `New variant of ${product.name}`}
      subtitle={
        variant
          ? 'Price changes apply to new documents only.'
          : 'A size, colour or finish with its own SKU, barcode and price.'
      }
      busy={save.isPending}
      submitLabel={variant ? 'Save changes' : 'Add variant'}
      onClose={onClose}
      onSubmit={submit}
    >
      <VariantFields
        control={control}
        setValue={setValue}
        index={0}
        attributes={defs}
        baseSymbol={product.baseUnit.symbol}
        trackInventory={product.trackInventory}
        canPrice={canPrice}
        canViewCost={canViewCost}
        onSuggestSku={() =>
          suggestSku(
            product.code,
            getValues('variants.0.nameSuffix'),
            variant ? 1 : product.variants.length,
          )
        }
      />
      {variant && (
        <Controller
          control={control}
          name="variants.0.isActive"
          render={({ field }) => (
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
              }
              label="Active — shown in search and on new documents"
            />
          )}
        />
      )}
    </FormDialog>
  );
}

// --- alternate units ---------------------------------------------------------------------

const unitsDialogSchema = z.object({ units: z.array(unitRowSchema).max(20) });

export function UnitsDialog({
  open,
  product,
  onClose,
}: {
  open: boolean;
  product: Product;
  onClose: () => void;
}) {
  const notify = useNotify();
  const qc = useQueryClient();
  const units = useUnits();
  const base = units.data?.find((u) => u.id === product.baseUnit.id) ?? {
    ...product.baseUnit,
    isActive: true,
    version: 1,
  };
  const initial = () => ({ units: product.units.map(productUnitToRow) });
  const { control, handleSubmit, reset, setError } = useForm<{ units: UnitRow[] }>({
    resolver: zodResolver(unitsDialogSchema),
    defaultValues: initial(),
  });
  useEffect(() => {
    if (open) reset(initial());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when the dialog opens
  }, [open]);

  const save = useMutation({
    mutationFn: (rows: UnitRow[]) =>
      unwrap(
        api.PUT('/products/{id}/units', {
          params: { path: { id: product.id } },
          body: { units: toUnitInputs(rows, product.baseUnit.id), version: product.version },
        }),
      ),
    onSuccess: async (saved) => {
      notify.success('Pack sizes saved');
      qc.setQueryData(catalogKeys.product(saved.id), saved);
      await qc.invalidateQueries({ queryKey: catalogKeys.products });
      onClose();
    },
    onError: (e) => notify.error(e),
  });

  return (
    <FormDialog
      open={open}
      maxWidth="md"
      title="Pack sizes"
      subtitle={`How many ${product.baseUnit.name.toLowerCase()} (${product.baseUnit.symbol}) each bigger unit holds. Stock stays in ${product.baseUnit.symbol}.`}
      busy={save.isPending}
      submitLabel="Save pack sizes"
      onClose={onClose}
      onSubmit={() =>
        void handleSubmit((v) => {
          const errs = unitRowErrors(v.units, base);
          for (const e of errs)
            setError(`units.${e.index}.${e.field}`, { type: 'rule', message: e.message });
          if (errs.length === 0) save.mutate(v.units);
        })()
      }
    >
      <UnitRowsEditor control={control} units={units.data ?? []} base={base} />
    </FormDialog>
  );
}
